import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { createSelector } from 'reselect';
import { RootState } from '../../../store/store';
import { Recommendation, PillarData } from '../types/api';

export interface ActionPlanTaskKey {
  recommendation_template_id: number;
  taskId?: number;
  task_index?: number;
}

interface CultureHubInterventionsState {
  plan: Recommendation[];
  deletedActionPlanIds: number[];
  selectedPillar: string;
  openPillarDialogKey: string | null;
  // Pillars from API (source of truth)
  pillars: Record<string, PillarData>;
}

const initialState: CultureHubInterventionsState = {
  plan: [],
  deletedActionPlanIds: [],
  selectedPillar: '',
  openPillarDialogKey: null,
  pillars: {},
};

const cultureHubInterventionsSlice = createSlice({
  name: 'cultureHubInterventions',
  initialState,
  reducers: {
    setPlan(state, action: PayloadAction<Recommendation[]>) {
      state.plan = action.payload;
    },
    addRecommendationToPlan(state, action: PayloadAction<Recommendation>) {
      const itemKey = `${action.payload.recommendation_template_id ?? 'unknown'}_${action.payload.task_index ?? 0}`;
      const exists = state.plan.some(
        (p) =>
          `${p.recommendation_template_id ?? 'unknown'}_${p.task_index ?? 0}` ===
          itemKey
      );
      if (!exists) {
        state.plan.push(action.payload);
      }
    },
    removeRecommendationFromPlan(
      state,
      action: PayloadAction<string | number | { pillar_key?: string; task_index?: number; recommendation_template_id?: number }>
    ) {
      const identifier = action.payload;
      state.plan = state.plan.filter((item) => {
        if (typeof identifier === 'object' && identifier !== null) {
          const rec = identifier as { pillar_key?: string; task_index?: number; recommendation_template_id?: number };
          if (
            typeof rec.recommendation_template_id === 'number' &&
            item.recommendation_template_id === rec.recommendation_template_id &&
            (item as any).task_index === (rec.task_index ?? 0)
          ) {
            return false;
          }
          if (
            rec.pillar_key &&
            (item as any).pillar_key === rec.pillar_key &&
            (item as any).task_index === (rec.task_index ?? 0)
          ) {
            return false;
          }
          return true;
        }
        if ((item as any).task_id === identifier) {
          return false;
        }
        if (item.recommendation_template_id === identifier) {
          return false;
        }
        return true;
      });
    },
    updateRecommendation(
      state,
      action: PayloadAction<{
        identifier: string | number;
        updates: Partial<Recommendation>;
      }>
    ) {
      const { identifier, updates } = action.payload;
      state.plan = state.plan.map((item) => {
        const itemId = item.recommendation_template_id || item.title;
        if (itemId === identifier) {
          return { ...item, ...updates };
        }
        return item;
      });
    },
    markActionPlanForDeletion(state, action: PayloadAction<number>) {
      if (!state.deletedActionPlanIds.includes(action.payload)) {
        state.deletedActionPlanIds.push(action.payload);
      }
    },
    clearDeletedActionPlanIds(state) {
      state.deletedActionPlanIds = [];
    },
    setSelectedPillar(state, action: PayloadAction<string>) {
      state.selectedPillar = action.payload;
    },
    openPillarDialog(state, action: PayloadAction<string>) {
      state.openPillarDialogKey = action.payload;
    },
    closePillarDialog(state) {
      state.openPillarDialogKey = null;
    },
    resetPlanFromServer(state, action: PayloadAction<Recommendation[]>) {
      state.plan = action.payload;
      state.deletedActionPlanIds = [];
    },
    resetInterventions() {
      return initialState;
    },
    setPillars(state, action: PayloadAction<Record<string, PillarData>>) {
      state.pillars = action.payload;
    },
  },
});

export const {
  setPlan,
  addRecommendationToPlan,
  removeRecommendationFromPlan,
  updateRecommendation,
  markActionPlanForDeletion,
  clearDeletedActionPlanIds,
  setSelectedPillar,
  openPillarDialog,
  closePillarDialog,
  resetPlanFromServer,
  resetInterventions,
  setPillars,
} = cultureHubInterventionsSlice.actions;

// Base selector
const selectInterventionsState = (state: RootState) => state.cultureHubInterventions;

// Plan selectors
export const getPlan = createSelector(
  [selectInterventionsState],
  (state): Recommendation[] => state.plan
);

export const getDeletedActionPlanIds = createSelector(
  [selectInterventionsState],
  (state): number[] => state.deletedActionPlanIds
);

export const getInterventionsSelectedPillar = createSelector(
  [selectInterventionsState],
  (state): string => state.selectedPillar
);

export const getOpenPillarDialogKey = createSelector(
  [selectInterventionsState],
  (state): string | null => state.openPillarDialogKey
);

export const getSelectedActionPlanTaskKeys = createSelector(
  [getPlan],
  (plan): ActionPlanTaskKey[] => {
    const keys: ActionPlanTaskKey[] = [];
    plan.forEach((item) => {
      if (item.recommendation_template_id) {
        keys.push({
          recommendation_template_id: item.recommendation_template_id,
          task_index: item.task_index ?? 0,
        });
      }
    });
    return keys;
  }
);

// Pillars - direct from API
export const getPillarsMap = createSelector(
  [selectInterventionsState],
  (state): Record<string, PillarData> => state.pillars
);

// Pillars as array for rendering
export const getPillars = createSelector(
  [getPillarsMap],
  (pillarsMap): PillarData[] => Object.values(pillarsMap)
);

// Filtered pillars based on selection
export const getFilteredPillars = createSelector(
  [getPillars, getInterventionsSelectedPillar],
  (pillars, selectedPillar): PillarData[] => {
    if (!selectedPillar) {
      return pillars;
    }
    return pillars.filter((p) => p.name === selectedPillar);
  }
);

// Get recommendations for the dialog from filtered pillars
export const getRecommendationsByPillarForDialog = createSelector(
  [getFilteredPillars],
  (filteredPillars): Record<string, Recommendation[]> | undefined => {
    if (!filteredPillars.length) {
      return undefined;
    }
    const result: Record<string, Recommendation[]> = {};
    filteredPillars.forEach((p) => {
      result[p.key] = p.recommendations || [];
    });
    return Object.keys(result).length > 0 ? result : undefined;
  }
);

export default cultureHubInterventionsSlice;
